/**
 * BackorderInvoiceClosePanel
 * --------------------------
 * Final-stage admin controls for a backorder:
 *   • Invoice info: number, total billed, sent at, paid at
 *   • Close Order button — moves status → fulfilled when all items shipped
 *     + invoice has been sent. Override available for one-off cases.
 *
 * Lives in the Details tab below Recipients. Hides the Close button unless
 * status is at least `shipped` or `partially_shipped` so we can't close
 * orders that haven't even been awarded.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Receipt, Save, CheckCircle2, AlertTriangle, Loader2, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

const CAN_CLOSE_STATUSES = new Set(["shipped", "partially_shipped", "delivered"]);

export default function BackorderInvoiceClosePanel({ backorder, token, onRefresh }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [invoiceNumber, setInvoiceNumber] = useState(backorder.invoice_number || "");
  const [invoiceTotal, setInvoiceTotal] = useState(backorder.invoice_total_billed ?? "");
  const [invoiceSent, setInvoiceSent] = useState(backorder.invoice_sent_at?.slice(0, 10) || "");
  const [invoicePaid, setInvoicePaid] = useState(backorder.invoice_paid_at?.slice(0, 10) || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    setInvoiceNumber(backorder.invoice_number || "");
    setInvoiceTotal(backorder.invoice_total_billed ?? "");
    setInvoiceSent(backorder.invoice_sent_at?.slice(0, 10) || "");
    setInvoicePaid(backorder.invoice_paid_at?.slice(0, 10) || "");
    setDirty(false);
  }, [backorder.id, backorder.invoice_number, backorder.invoice_total_billed, backorder.invoice_sent_at, backorder.invoice_paid_at]);

  const saveInvoice = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/api/admin/backorders/${backorder.id}/invoice`,
        {
          invoice_number: invoiceNumber || null,
          invoice_total_billed: invoiceTotal === "" ? null : invoiceTotal,
          invoice_sent_at: invoiceSent || null,
          invoice_paid_at: invoicePaid || null,
        },
        { headers },
      );
      toast.success("Invoice details saved.");
      setDirty(false);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const isClosed = (backorder.status || "").toLowerCase() === "fulfilled";
  const canCloseNow = CAN_CLOSE_STATUSES.has((backorder.status || "").toLowerCase());

  const closeOrder = async (force = false) => {
    setClosing(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/close`,
        { force },
        { headers },
      );
      toast.success("Backorder closed.", {
        description: r.data?.warnings?.length
          ? `Forced — ${r.data.warnings.join(", ")}`
          : "Status now Fulfilled. Moved to Completed Orders.",
      });
      onRefresh?.();
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not close.";
      if (detail.includes("force=true")) {
        // Surface override option
        toast.warning(detail, {
          action: { label: "Close anyway", onClick: () => closeOrder(true) },
          duration: 8000,
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setClosing(false);
    }
  };

  return (
    <section
      className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3"
      data-testid="invoice-close-panel"
    >
      <header className="flex items-center justify-between">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-900 inline-flex items-center gap-1.5">
            <Receipt className="w-3.5 h-3.5" /> Invoice Audit (Internal)
          </h3>
          <p className="text-[10px] text-slate-500 mt-0.5">
            Tracking only — no email is sent. Invoicing is handled by your finance partner.
          </p>
        </div>
        {isClosed && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-slate-700 bg-slate-200 border border-slate-300 rounded px-2 py-0.5" data-testid="close-status-closed">
            <Lock className="w-3 h-3" /> Closed
            {backorder.closed_at && ` · ${new Date(backorder.closed_at).toLocaleDateString()}`}
          </span>
        )}
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-white border border-emerald-100 rounded-lg p-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Invoice #</label>
          <Input
            value={invoiceNumber}
            onChange={(e) => { setInvoiceNumber(e.target.value); setDirty(true); }}
            placeholder="INV-2026-0042"
            className="h-8 text-sm font-mono"
            data-testid="invoice-number-input"
            disabled={isClosed}
          />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Total billed ($)</label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={invoiceTotal}
            onChange={(e) => { setInvoiceTotal(e.target.value); setDirty(true); }}
            placeholder="0.00"
            className="h-8 text-sm text-right"
            data-testid="invoice-total-input"
            disabled={isClosed}
          />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Invoice sent</label>
          <Input
            type="date"
            value={invoiceSent}
            onChange={(e) => { setInvoiceSent(e.target.value); setDirty(true); }}
            className="h-8 text-sm"
            data-testid="invoice-sent-input"
            disabled={isClosed}
          />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Invoice paid</label>
          <Input
            type="date"
            value={invoicePaid}
            onChange={(e) => { setInvoicePaid(e.target.value); setDirty(true); }}
            className="h-8 text-sm"
            data-testid="invoice-paid-input"
            disabled={isClosed}
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button
          size="sm"
          onClick={saveInvoice}
          disabled={!dirty || saving || isClosed}
          variant="outline"
          className="gap-1.5 text-xs"
          data-testid="invoice-save-btn"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {dirty ? "Save invoice" : "Saved"}
        </Button>

        {!isClosed && (
          <Button
            size="sm"
            onClick={() => closeOrder(false)}
            disabled={closing || !canCloseNow}
            className="bg-emerald-600 hover:bg-emerald-700 gap-1.5 text-xs"
            title={
              !canCloseNow
                ? "Order must be shipped before it can be closed."
                : "Mark this backorder as fulfilled and move it to Completed Orders."
            }
            data-testid="close-order-btn"
          >
            {closing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Close Order
          </Button>
        )}
      </div>

      {!isClosed && canCloseNow && !backorder.invoice_sent_at && (
        <p className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1 inline-flex items-center gap-1" data-testid="close-warning-no-invoice">
          <AlertTriangle className="w-3 h-3" />
          Invoice audit box not checked — closing will require an override.
        </p>
      )}
    </section>
  );
}
